import h from 'hyperscript';
import { fromExtent } from 'ol/geom/Polygon';
import GeoJSON from 'ol/format/GeoJSON';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { on, unByKey } from 'cadenza/utils/event-util';
import { STATE, VIEW } from 'cadenza/map-dashboard/event-types';
import { storeRequestParameterValue } from 'cadenza/api-client/processing-chain-api';
import { createFeatureSelectionData } from 'cadenza/map/layer-utils';
import { IFrameView } from 'cadenza/map-dashboard/view/iframe-view';
import { EXECUTION_CONFIRMED } from 'cadenza/api-client/api-constants';
import { TableViewToolbar } from 'cadenza/map-dashboard/table-view/table-view-toolbar';
import { TableViewApi, TableViewMessageTypes } from 'cadenza/map-dashboard/table-view/table-view-api';
import { parseCommaSeparateAttribute } from 'cadenza/result-table/result-table-utils';

import './table-view.css';

const name = 'd-table-view';

export class TableView extends IFrameView {

  constructor () {
    super({ postMessageApi: new TableViewApi() });
    this.classList.add(name);
    this._tableOverlay = h('.d-table-view--overlay', {
      hidden: true
    });
    this.append(this._tableOverlay);
  }

  connectedCallback () {
    super.connectedCallback();
    this.dashboard = this.closest('d-map-dashboard');
    this.insertBefore(new TableViewToolbar(), this.childNodes[0]);

    this._eventKeys = [
      on(this.dashboard, VIEW.TOGGLE_TABLE_VISIBILITY, (event) => this._onToggleTableVisibility(event.detail))
    ];
  }

  get downloadOptions () {
    return parseCommaSeparateAttribute(this.getAttribute('download-options'));
  }

  _initEvents () {
    unByKey(this._eventKeys);
    this._eventKeys = [
      on(this.dashboard, STATE.SELECTION_CHANGE, event => this._onSelectionChanged(event.detail)),
      on(this.dashboard, STATE.CONDITION_CHANGE, event => this._onConditionChanged(event.detail)),
      on(this.dashboard, STATE.EXTENT_CHANGE, () => this._onExtentChanged()),
      on(this.dashboard, VIEW.TOGGLE_TABLE_VISIBILITY, event => this._onToggleTableVisibility(event.detail)),
      on(this.dashboard, VIEW.TOGGLE_TABLE_OVERLAY, event => this._onToggleTableOverlay(event.detail)),
      on(this.api, TableViewMessageTypes.ROW_SELECTION_CHANGED, event => this._onResultTableSelectionChanged(event))
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  _init ({ src } = {}) {
    this.hidden = false;
    this._initEvents();
    return updateUrlWithConditionUuid(src, this._geometryAttribute, this._geometrySrs).then(updatedSrc => {
      const withExecutionConfirmed = updateUrlWithParams(updatedSrc, { [EXECUTION_CONFIRMED]: true });
      this.initializeIframe(withExecutionConfirmed);
    });
  }

  onFrameLoad () {
    super.onFrameLoad();
    this._onSelectionChanged(this.dashboard.selectedFeatureIDs);
  }

  _onSelectionChanged (newSelectedFeatureIDs) {
    if (!this.isDispatching && newSelectedFeatureIDs[this._viewId]) {
      this.api.stateChanged(STATE.SELECTION_CHANGE, newSelectedFeatureIDs[this._viewId]);
    }
  }

  _onConditionChanged (eventDetail) {
    if (eventDetail.viewId && this._viewId !== eventDetail.viewId) {
      return;
    }
    this.src = updateUrlWithParams(this.src, { conditionValuesSetHash: eventDetail.conditionValuesSetHash });
    this.api.stateChanged(STATE.CONDITION_CHANGE, this.src);
  }

  _onExtentChanged () {
    setTimeout(() => {
      if (this._isDisabled) {
        return;
      }
      return this._updateTableData();
    }, 0);
  }

  _updateTableData () {
    return updateUrlWithConditionUuid(this.src, this._geometryAttribute, this._geometrySrs)
      .then(updatedUrl => {
        this.src = updatedUrl;
        this.api.stateChanged(STATE.EXTENT_CHANGE, updatedUrl);
      });
  }

  _onToggleTableVisibility (eventDetail) {
    if (eventDetail.isVisible) {
      this._viewId = eventDetail.viewId;
      this._geometryAttribute = eventDetail.geometryAttribute;
      this._geometrySrs = eventDetail.geometrySrs;
      this._tableOverlay.hidden = true;
      this.hidden = false;
      this.notify(VIEW.LAYOUT_CHANGED, { shouldDashboardActivate: true });
      /*
       * The updateMapSize event will result in a map.updateSize in the map-view, which is essentially a re-rendering of the map using the
       * new size. Rendering in OL5 is done using window.requestAnimationFrame and listeners such as moveend are called ASYNC after re-render.
       * This is the reason why we put the initialization of the iframe on the event loop (using setTimeout 0), to be executed after
       * all others events (such as moveend) already present on the event loop have finished executing.
       */
      setTimeout(() => {
        this._init({
          src: cadenzaUrl(eventDetail.tableUrl)
        });
      }, 0);
    } else if (!eventDetail.viewId || eventDetail.viewId === this._viewId) {
      unByKey(this._eventKeys);
      this.hidden = true;
      this.notify(VIEW.LAYOUT_CHANGED, { shouldDashboardActivate: false });
      this._eventKeys = [
        on(this.dashboard, VIEW.TOGGLE_TABLE_VISIBILITY, (event) => this._onToggleTableVisibility(event.detail))
      ];
    }
  }

  _onToggleTableOverlay (event) {
    const viewId = event.viewId;
    const isOverlayed = event.isOverlayed;
    const isDisabled = event.isDisabled;

    if (this._viewId !== viewId) {
      return;
    }
    if (isDisabled != null) {
      if (this._isDisabled && !isDisabled) {
        this._updateTableData();
      }
      this._isDisabled = isDisabled;
    }
    if (isOverlayed != null) {
      this._tableOverlay.hidden = !isOverlayed && !this._isDisabled;
    }
  }

  _onResultTableSelectionChanged (event) {
    event.stopPropagation();
    const newEventData = createFeatureSelectionData([ this._viewId ], event.detail);
    this.notify(STATE.SELECTION_CHANGE, newEventData);
  }

  close () {
    this.parentElement.removeChild(this);
  }

}

function updateUrlWithConditionUuid (iFrameSrcUrl, geometryAttribute, geometrySrs) {
  const { mapExtent, mapSrs } = getParamsFromUrl(window.location.href, 'mapExtent', 'mapSrs');

  return storeBBoxConditionToCache({
    mapSrs,
    mapExtent,
    geometryAttribute,
    geometrySrs
  }).then(conditionUUid => {
    const conditionUuidParam = { 'conditionRef["net.disy.selector.bounding_box"]': conditionUUid };
    return updateUrlWithParams(iFrameSrcUrl, conditionUuidParam);
  });
}

function storeBBoxConditionToCache ({ mapSrs, mapExtent, geometryAttribute, geometrySrs } = {}) {
  const mapExtentAsGeoJsonString = mapExtent ? JSON.stringify(extentToGeoJson(mapExtent)) : null;
  const bboxValue = (mapSrs === geometrySrs) ? [ mapExtentAsGeoJsonString, 'bbox', geometrySrs ] : [ mapExtentAsGeoJsonString, 'bbox', mapSrs, geometrySrs ];
  const bboxCondition = {
    id: 'net.disy.selector.bbox_condition',
    attributes: [ geometryAttribute ],
    type: 'PolygonCondition',
    custom: true,
    value: [ bboxValue ]
  };
  return storeRequestParameterValue(bboxCondition);
}

function extentToGeoJson (mapExtent) {
  const extent = mapExtent.split(',').map(parseFloat);
  const polygon = fromExtent(extent);
  return new GeoJSON().writeGeometryObject(polygon);
}

function getParamsFromUrl (url, ...paramNamesToGet) {
  const [ , searchUrl ] = url.split('?');
  const currentParams = new URLSearchParams(searchUrl);
  const ret = {};
  paramNamesToGet.forEach(paramName => {
    ret[paramName] = currentParams.get(paramName);
  });
  return ret;
}

function updateUrlWithParams (urlToUpdate, newParams) {
  const [ baseUrl, searchUrl ] = urlToUpdate.split('?');
  const currentParams = new URLSearchParams(searchUrl);
  Object.entries(newParams).forEach(([ key, value ]) => currentParams.set(key, value));
  return `${baseUrl}?${currentParams.toString()}`;
}

customElements.define(name, TableView);
